import circle from '@turf/circle';
import { geojsonToWKT } from '@terraformer/wkt';
import type { MultiPolygon, Polygon } from 'geojson';
import { Observable, of } from 'rxjs';
// Type-only: keeps this util free of a runtime edge into HttpClient, so the
// pure helpers below stay importable from scripts and specs.
import type { IsochroneService } from '../services/isochrone.service';

export type TradeAreaKind = 'drive' | 'radius';

export interface TradeAreaShape {
  kind: TradeAreaKind;
  /** Minutes for a drive-time area, miles for a radius. */
  value: number;
}

export type LngLatPair = [number, number];

export function tradeAreaLabel({ kind, value }: TradeAreaShape): string {
  return kind === 'drive' ? `${value}-min drive` : `${value}-mi radius`;
}

export function radiusAreaGeometry(
  lng: number,
  lat: number,
  radiusMiles: number
): Polygon {
  return circle([lng, lat], radiusMiles, { units: 'miles' }).geometry;
}

/**
 * The one way every trade-area tool turns a point and a shape into a polygon:
 * the map popup's trade-area row, the compare tool and the trade-area pages.
 *
 * A radius is a synchronous turf circle. A drive time asks the Mapbox
 * Isochrone API and emits null when Mapbox has no polygon for the spot; it is
 * deliberately never replaced by a circle here, because a straight-line circle
 * presented as a drive time is a wrong answer. Callers that want a fallback
 * (the sales-forecast pages) decide that themselves.
 */
export function buildTradeAreaGeometry(
  isochrone: IsochroneService,
  [lng, lat]: LngLatPair,
  shape: TradeAreaShape
): Observable<Polygon | MultiPolygon | null> {
  return shape.kind === 'drive'
    ? isochrone.fetchDriveTimeArea(lng, lat, shape.value)
    : of(radiusAreaGeometry(lng, lat, shape.value));
}

/**
 * The backend contract only takes MULTIPOLYGON WKT (see
 * CreateCellRequest.multipolygonWkt), while Mapbox isochrones and turf's
 * circle come back as a plain Polygon. Wrapped as geometry rather than by
 * editing the WKT string, so a polygon with holes stays valid.
 */
export function toMultipolygonWkt(geometry: Polygon | MultiPolygon): string {
  return geojsonToWKT(
    geometry.type === 'MultiPolygon'
      ? geometry
      : { type: 'MultiPolygon', coordinates: [geometry.coordinates] }
  );
}
